/* Cap World: isolated, progressively enhanced desktop menu tabs. */
(() => {
  'use strict';
  if (customElements.get('cw-team-menu')) return;

  class CWTeamMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.closest('.js__header__clone')) return;
      this.tabs = Array.from(this.querySelectorAll('[data-cw-team-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-cw-team-panel]'));
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('focus', () => this.select(index), options);
        tab.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          this.clearHover();
          this.hoverTimer = setTimeout(() => this.select(index), 90);
        }, options);
        tab.addEventListener('pointerleave', () => this.clearHover(), options);
        tab.addEventListener('keydown', (event) => this.onTabKey(event, index), options);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const disclosure = this.closest('hover-disclosure');
        const trigger = disclosure?.querySelector('[data-top-link]');
        if (!disclosure || typeof disclosure.hideDisclosure !== 'function') return;
        event.preventDefault();
        event.stopPropagation();
        this.clearHover();
        trigger?.focus({ preventScroll: true });
        disclosure.hideDisclosure();
      }, options);
      this.addEventListener('error', (event) => {
        if (event.target instanceof HTMLImageElement) event.target.hidden = true;
      }, { ...options, capture: true });
      if (this.classList.contains('cw-team-menu--brands') && window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[data-cw-team-panel]');
          const index = this.panels.indexOf(panel);
          if (index !== -1) this.select(index);
        }, options);
      }

      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[index].id);
      });
      this.activeIndex = null;
      this.select(0);
      this.classList.add('is-ready');
    }

    select(index, focus = false) {
      this.clearHover();
      const changed = this.activeIndex !== null && this.activeIndex !== index;
      if (this.activeIndex !== index) this.cancelPanelAnimation();
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[i].hidden = !active;
      });
      this.activeIndex = index;
      if (changed) this.animatePanel(this.panels[index]);
      if (focus) {
        const tab = this.tabs[index];
        tab.focus({ preventScroll: true });
        if (this.classList.contains('cw-team-menu--brands')) {
          const list = tab.parentElement;
          const bounds = tab.getBoundingClientRect();
          const viewport = list.getBoundingClientRect();
          if (bounds.top < viewport.top) list.scrollTop -= viewport.top - bounds.top;
          else if (bounds.bottom > viewport.bottom) list.scrollTop += bounds.bottom - viewport.bottom;
        }
      }
    }

    animatePanel(panel) {
      if (!this.closest('hover-disclosure')?.classList.contains('is-visible')) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || typeof panel.animate !== 'function') return;
      const animation = panel.animate([
        { opacity: 0, transform: 'translateY(2px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ], { duration: 120, easing: 'ease-out' });
      this.panelAnimation = animation;
      animation.onfinish = () => {
        if (this.panelAnimation === animation) this.panelAnimation = null;
      };
    }

    cancelPanelAnimation() {
      this.panelAnimation?.cancel();
      this.panelAnimation = null;
    }

    onTabKey(event, index) {
      let next;
      if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowUp') next = (index + this.tabs.length - 1) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next !== undefined) {
        event.preventDefault();
        this.select(next, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.panels[index].querySelector('a[href]')?.focus({ preventScroll: true });
      }
    }

    clearHover() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }

    disconnectedCallback() {
      this.clearHover();
      this.cancelPanelAnimation();
      this.controller?.abort();
      this.controller = null;
      this.activeIndex = null;
    }
  }

  customElements.define('cw-team-menu', CWTeamMenu);
})();
